// "Hide offline devices" — the setting, and the filter it drives.
//
// The setting is a plain localStorage flag so it survives a reload and an app
// restart; it is per-browser/per-install, not per-user, which matches every
// other UI preference here (see utils/orderCache, platform.uiMode). Mobile and
// desktop read the SAME key, so the choice follows the user across shells.
//
// ── The filter must agree with the indicator on the card ───────────────────
// A checkbox that hides devices the dot still shows green reads as broken, so
// this decides liveness exactly the way the two indicators do:
//
//   • the LIST ROW's timestamp, aged through ageOf() — this is what
//     DeviceStatusDot uses, and it costs nothing (the row is already here).
//     ⚠️ the field name differs by endpoint: room devices say
//     lastUpdatedDateTime, common devices LastUpdatedDateTime.
//   • no timestamp on the row (GetListofDevicesbyUserId and the favourites
//     list return none) — fall back to getStatusCached, the SAME 45 s-cached
//     lookup LivenessBadge fills, so the filter adds no HTTP calls of its own.
//
// Two deliberate choices, both to avoid showing an empty screen:
//
//   • a device is hidden only once it is KNOWN offline. The fallback resolves
//     one request at a time, so treating "not answered yet" as offline would
//     blank the grid on every open and fill it back in.
//   • in offline mode the server is unreachable and every status resolves to
//     UNKNOWN by definition — nothing is known offline, so nothing is hidden.
//     Filtering there would hide the entire list exactly when the user still
//     needs it (BLE control keeps working).

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ageOf, getStatusCached, hasNoHeartbeat, ONLINE, ONLINE_WINDOW_MS,
} from '../devices/deviceStatus';
import { isOffline } from '../offline/offlineMode';
import { getHubStatus, hubOfNode, onAnyHubChange, watchHub } from '../ble/mesh/hubStatus';
import { isNativePlatform } from '../platform';

const KEY = 'sli_hide_offline';

export function loadHideOffline() {
  try { return localStorage.getItem(KEY) === '1'; } catch (e) { return false; }
}

/*
 * ── One checkbox, many readers ─────────────────────────────────────────────
 *
 * ⚠️ localStorage IS NOT REACTIVE, and this hook used to hold nothing but a
 * local `useState` seeded from it. That was survivable while every screen drew
 * its own checkbox — each one only had to update itself. It stops working the
 * moment there is a SINGLE checkbox governing everything: the desktop header
 * and the device grid below it are mounted at the same time, so ticking the box
 * in one changed localStorage and left the other rendering its stale copy until
 * something else happened to re-render it.
 *
 * (The `storage` event is no help either — browsers fire it in OTHER tabs, not
 * the one that made the change.)
 *
 * So subscribers are kept here and notified on every write. Every
 * `useHideOffline()` in the tree moves together, in both shells.
 */
const listeners = new Set();

/**
 * Watch the setting. Returns an unsubscribe function.
 *
 * Exported rather than kept inside the hook so the behaviour that makes the
 * single checkbox work is testable on its own — the repo has no React testing
 * library, and "does every screen follow the one control" is exactly the thing
 * worth a test.
 */
export function subscribeHideOffline(fn) {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

export function saveHideOffline(on) {
  try { localStorage.setItem(KEY, on ? '1' : '0'); } catch (e) { /* ignore */ }
  // A throwing listener must not stop the others from hearing about it.
  listeners.forEach((fn) => { try { fn(!!on); } catch (e) { /* ignore */ } });
}

/** The persisted checkbox state: [hideOffline, setHideOffline]. */
export function useHideOffline() {
  const [hide, setHide] = useState(loadHideOffline);

  // Follow every other copy of this setting, wherever it was changed.
  useEffect(() => {
    const off = subscribeHideOffline(setHide);
    // A late-mounting screen must not trust the value it captured at first
    // render — the box may have been ticked before it existed.
    setHide(loadHideOffline());
    return off;
  }, []);

  const set = useCallback((on) => {
    // No setHide here: saveHideOffline notifies every subscriber INCLUDING this
    // one, so setting it directly would just be the same update twice.
    saveHideOffline(!!on);
  }, []);

  return [hide, set];
}

const unoOf = (d) => String((d && (d.UniqueNumber ?? d.uniqueNumber)) ?? '');

// A Bluetooth MESH lamp group has no heartbeat of its own, but WITHOUT A RADIO
// (browser / desktop) it is reachable only through the location's hub — and
// with that hub down its card says "Hub offline" and greys out. That is
// offline, and "Hide offline" hid everything else but kept it on screen.
// Same fact the card draws (useBleNode → noteNodeHub → hubStatus); a phone
// drives the lamps over its own Bluetooth, so there it is never hidden.
function bleGroupUnreachable(d) {
  if (isNativePlatform()) return false;
  const hub = hubOfNode(unoOf(d));
  return !!hub && getHubStatus(hub).live === false;
}

// The row's own heartbeat, corrected for the server's clock — null when the
// endpoint didn't send one.
function rowAgeMs(d) {
  const raw = d && (d.lastUpdatedDateTime ?? d.LastUpdatedDateTime);
  if (!raw) return null;
  const t = Date.parse(String(raw).trim().replace(' ', 'T'));
  return Number.isNaN(t) ? null : ageOf(t);
}

// How often the filter re-evaluates while it is on, so a device that comes back
// reappears without a manual refresh. Longer than the 45 s status-cache TTL, so
// a fallback pass is a real refetch rather than a cache echo.
const REFRESH_MS = 60000;

/**
 * Filters a device list down to the ones known to be online.
 *
 * @param devices  the full list (returned untouched when `enabled` is false)
 * @param enabled  the checkbox
 * @returns { devices, hiddenCount }
 */
export function useOnlineOnly(devices, enabled) {
  // uno -> true (online) | false (offline / unknown), for rows with no
  // timestamp. Absent = not resolved yet, which shows.
  const [known, setKnown] = useState({});
  // Bumped on each pass so row-based ages are recomputed as they grow stale.
  const [pass, setPass] = useState(0);

  // Only the rows that carry no timestamp need asking about.
  const needFetch = useMemo(
    () => (devices || []).filter((d) => unoOf(d) && !hasNoHeartbeat(d) && rowAgeMs(d) == null).map(unoOf),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [devices],
  );
  const fetchKey = needFetch.join(',');
  // Lamp groups are not in needFetch, but their hubs are touched every pass.
  const bleKey = (devices || []).filter(hasNoHeartbeat).map(unoOf).join(',');

  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;

    const resolve = () => {
      needFetch.forEach((uno) => {
        getStatusCached(uno)
          .catch(() => null)
          .then((v) => {
            if (!alive) return;
            const online = v === ONLINE;
            setKnown((prev) => (prev[uno] === online ? prev : { ...prev, [uno]: online }));
          });
      });
      setPass((n) => n + 1);
    };

    // A hidden lamp group's card is unmounted, so nothing else watches its hub:
    // touch each one on every pass (re-checked only when stale, one shared REST
    // read) so a hub that comes back brings its groups back.
    const touchHubs = () => {
      (devices || []).forEach((d) => {
        if (!hasNoHeartbeat(d)) return;
        const hub = hubOfNode(unoOf(d));
        if (hub) watchHub(hub, undefined, () => {})();
      });
    };

    resolve();
    touchHubs();
    const id = setInterval(() => { resolve(); touchHubs(); }, REFRESH_MS);
    const offHub = onAnyHubChange(() => { if (alive) setPass((n) => n + 1); });
    return () => { alive = false; clearInterval(id); offHub(); };
    // fetchKey stands in for the list identity — the array itself is new each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, fetchKey, bleKey]);

  return useMemo(() => {
    const all = devices || [];
    if (!enabled || isOffline()) return { devices: all, hiddenCount: 0 };
    const shown = all.filter((d) => {
      // No heartbeat to judge (a BLE lamp group): hidden only when its hub is
      // known down and nothing else can reach it (bleGroupUnreachable).
      if (hasNoHeartbeat(d)) return !bleGroupUnreachable(d);
      const age = rowAgeMs(d);
      if (age != null) return age < ONLINE_WINDOW_MS;
      return known[unoOf(d)] !== false; // hide only what we KNOW is offline
    });
    return { devices: shown, hiddenCount: all.length - shown.length };
    // `pass` is a deliberate dependency: it re-runs the age comparison on a timer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices, enabled, known, pass]);
}
